import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const WEEKDAYS_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const WEEKDAYS_LONG = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const HOURS = Array.from({ length: 15 }, (_, i) => i + 6); // 06:00 → 20:00
const HOUR_HEIGHT = 56;

function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay());
  return x;
}
function addDays(d, n) {
  const x = new Date(d); x.setDate(x.getDate() + n); return x;
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function eventTop(startISO) {
  const d = new Date(startISO);
  const hours = d.getHours() + d.getMinutes() / 60;
  return (hours - HOURS[0]) * HOUR_HEIGHT;
}
function eventHeight(startISO, endISO) {
  const s = new Date(startISO), e = new Date(endISO);
  const durMin = Math.max(30, (e - s) / 60000);
  return Math.max(28, (durMin / 60) * HOUR_HEIGHT);
}

export default function CalendarPage() {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("month"); // month | week | day
  const [cursor, setCursor] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/inspections");
      setRows(data);
    })();
  }, []);

  const eventsByDay = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const d = new Date(r.scheduled_start);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (map[key] ||= []).push(r);
    });
    return map;
  }, [rows]);

  const shift = (delta) => {
    const n = new Date(cursor);
    if (view === "month") n.setMonth(n.getMonth() + delta);
    else if (view === "week") n.setDate(n.getDate() + 7 * delta);
    else n.setDate(n.getDate() + delta);
    setCursor(n);
    setSelectedDay(null);
  };

  const label = useMemo(() => {
    if (view === "month") return `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
    if (view === "week") {
      const s = startOfWeek(cursor); const e = addDays(s, 6);
      return `${s.getDate()} ${MONTHS[s.getMonth()].slice(0, 3)} – ${e.getDate()} ${MONTHS[e.getMonth()].slice(0, 3)} ${e.getFullYear()}`;
    }
    return `${WEEKDAYS_LONG[cursor.getDay()]}, ${cursor.getDate()} de ${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  }, [view, cursor]);

  return (
    <Layout title="Calendário" subtitle="Visão mensal, semanal e diária das inspeções">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex border border-border rounded-md overflow-hidden">
          {[
            ["month", "Mês"], ["week", "Semana"], ["day", "Dia"],
          ].map(([v, lb]) => (
            <button
              key={v}
              onClick={() => { setView(v); setSelectedDay(null); }}
              data-testid={`cal-view-${v}`}
              className={`px-4 py-2 text-sm font-bold transition-colors ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
            >{lb}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="cal-prev"><CaretLeft size={16} /></Button>
          <div className="text-lg md:text-xl font-black tracking-tight px-2 min-w-[220px] text-center" style={{ fontFamily: "Manrope" }} data-testid="cal-label">{label}</div>
          <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="cal-next"><CaretRight size={16} /></Button>
          <Button variant="outline" onClick={() => { setCursor(new Date()); setSelectedDay(null); }} data-testid="cal-today">Hoje</Button>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-primary" />
          <span className="text-muted-foreground">Inspeção</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-amber-400" />
          <span className="text-muted-foreground">Visita técnica</span>
        </div>
      </div>

      {view === "month" && (
        <MonthView cursor={cursor} eventsByDay={eventsByDay} selectedDay={selectedDay} setSelectedDay={setSelectedDay} onOpenDay={(d) => { setCursor(d); setView("day"); }} />
      )}
      {view === "week" && (
        <WeekView cursor={cursor} rows={rows} onOpenDay={(d) => { setCursor(d); setView("day"); }} />
      )}
      {view === "day" && (
        <DayView cursor={cursor} rows={rows} />
      )}
    </Layout>
  );
}

function MonthView({ cursor, eventsByDay, selectedDay, setSelectedDay, onOpenDay }) {
  const days = useMemo(() => {
    const year = cursor.getFullYear(); const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const startWeekday = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < startWeekday; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [cursor]);

  const dayEvents = selectedDay
    ? eventsByDay[`${selectedDay.getFullYear()}-${selectedDay.getMonth()}-${selectedDay.getDate()}`] || []
    : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="tech-card p-4 lg:col-span-2">
        <div className="grid grid-cols-7 gap-px bg-border rounded-md overflow-hidden">
          {WEEKDAYS_SHORT.map((d) => <div key={d} className="label-tech text-muted-foreground p-3 text-center bg-card">{d}</div>)}
          {days.map((d, i) => {
            const key = d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : null;
            const events = key ? eventsByDay[key] || [] : [];
            const isToday = d && sameDay(new Date(), d);
            const isSelected = d && selectedDay && sameDay(d, selectedDay);
            return (
              <button
                key={i}
                onClick={() => d && setSelectedDay(d)}
                onDoubleClick={() => d && onOpenDay(d)}
                disabled={!d}
                className={`bg-card min-h-[90px] p-2 text-left transition-colors ${d ? "hover:bg-secondary" : "opacity-30"} ${isSelected ? "ring-2 ring-primary" : ""}`}
                data-testid={d ? `cal-day-${d.getDate()}` : undefined}
              >
                {d && (
                  <>
                    <div className={`text-sm font-bold ${isToday ? "text-primary" : ""}`}>{d.getDate()}</div>
                    <div className="mt-1 space-y-0.5">
                      {events.slice(0, 2).map((e) => {
                        const isVisit = e.kind === "site_visit";
                        return (
                          <div key={e.id} className={`text-[10px] px-1.5 py-0.5 rounded truncate ${isVisit ? "bg-amber-500/20 text-amber-400" : "bg-primary/20 text-primary"}`}>
                            {isVisit ? "◆ " : ""}{e.title}
                          </div>
                        );
                      })}
                      {events.length > 2 && <div className="text-[10px] text-muted-foreground">+{events.length - 2} mais</div>}
                    </div>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="tech-card p-6">
        <div className="label-tech text-muted-foreground">Dia selecionado</div>
        <h2 className="text-2xl font-extrabold tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>
          {selectedDay ? selectedDay.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" }) : "Clique em um dia"}
        </h2>
        <div className="mt-4 space-y-3">
          {selectedDay && dayEvents.length === 0 && <div className="text-muted-foreground text-sm">Nenhuma inspeção neste dia</div>}
          {dayEvents.map((e) => (
            <div key={e.id} className="border border-border rounded-md p-3">
              <div className="font-bold">{e.title}</div>
              <div className="text-xs text-muted-foreground mt-1 mono">{formatDateTime(e.scheduled_start)}</div>
              <div className="text-xs text-muted-foreground mt-1">{e.address_city ? `${e.address_city}/${e.address_state || ""}` : ""}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function WeekView({ cursor, rows, onOpenDay }) {
  const weekStart = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const eventsFor = (day) => rows.filter((r) => sameDay(new Date(r.scheduled_start), day));

  return (
    <div className="tech-card overflow-hidden">
      <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-border">
        <div className="p-2 border-r border-border"></div>
        {days.map((d) => {
          const isToday = sameDay(new Date(), d);
          return (
            <button key={d.toISOString()} onClick={() => onOpenDay(d)} className={`p-3 text-center border-r border-border last:border-r-0 hover:bg-secondary transition-colors ${isToday ? "bg-primary/10" : ""}`}>
              <div className="label-tech text-muted-foreground">{WEEKDAYS_SHORT[d.getDay()]}</div>
              <div className={`text-2xl font-black tracking-tight mt-1 ${isToday ? "text-primary" : ""}`} style={{ fontFamily: "Manrope" }}>{d.getDate()}</div>
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-[60px_repeat(7,1fr)] relative">
        <div className="border-r border-border">
          {HOURS.map((h) => (
            <div key={h} className="label-tech text-muted-foreground text-right pr-2 border-b border-border" style={{ height: HOUR_HEIGHT }}>{String(h).padStart(2, "0")}:00</div>
          ))}
        </div>
        {days.map((d, di) => (
          <div key={di} className="relative border-r border-border last:border-r-0">
            {HOURS.map((h) => <div key={h} className="border-b border-border" style={{ height: HOUR_HEIGHT }} />)}
            {eventsFor(d).map((e) => {
              const isVisit = e.kind === "site_visit";
              return (
                <div
                  key={e.id}
                  className={`absolute left-1 right-1 rounded-md p-2 text-xs overflow-hidden cursor-pointer transition-colors border-l-4 ${
                    isVisit
                      ? "bg-amber-500/20 border-amber-400 hover:bg-amber-500/30"
                      : "bg-primary/25 border-primary hover:bg-primary/35"
                  }`}
                  style={{ top: eventTop(e.scheduled_start), height: eventHeight(e.scheduled_start, e.scheduled_end) }}
                  data-testid={`week-event-${e.id}`}
                >
                  <div className="font-bold truncate">{isVisit ? "◆ " : ""}{e.title}</div>
                  <div className="text-[10px] text-muted-foreground mono truncate">
                    {new Date(e.scheduled_start).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function DayView({ cursor, rows }) {
  const dayEvents = rows.filter((r) => sameDay(new Date(r.scheduled_start), cursor))
    .sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="tech-card overflow-hidden lg:col-span-2">
        <div className="grid grid-cols-[60px_1fr] relative">
          <div className="border-r border-border">
            {HOURS.map((h) => (
              <div key={h} className="label-tech text-muted-foreground text-right pr-2 border-b border-border" style={{ height: HOUR_HEIGHT }}>{String(h).padStart(2, "0")}:00</div>
            ))}
          </div>
          <div className="relative">
            {HOURS.map((h) => <div key={h} className="border-b border-border" style={{ height: HOUR_HEIGHT }} />)}
            {dayEvents.map((e) => {
              const isVisit = e.kind === "site_visit";
              return (
                <div
                  key={e.id}
                  className={`absolute left-2 right-2 rounded-md p-3 overflow-hidden border-l-4 ${
                    isVisit ? "bg-amber-500/20 border-amber-400" : "bg-primary/25 border-primary"
                  }`}
                  style={{ top: eventTop(e.scheduled_start), height: eventHeight(e.scheduled_start, e.scheduled_end) }}
                  data-testid={`day-event-${e.id}`}
                >
                  <div className="font-bold text-sm">{isVisit ? "◆ " : ""}{e.title}</div>
                  <div className="text-xs text-muted-foreground mono mt-1">
                    {new Date(e.scheduled_start).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} –{" "}
                    {new Date(e.scheduled_end).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                  {e.address_city && <div className="text-xs text-muted-foreground mt-1">{e.address_city}/{e.address_state}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="tech-card p-6">
        <div className="label-tech text-muted-foreground">Missões do dia</div>
        <h2 className="text-2xl font-extrabold tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>{dayEvents.length} agendada{dayEvents.length !== 1 ? "s" : ""}</h2>
        <div className="mt-4 space-y-3">
          {dayEvents.length === 0 && <div className="text-muted-foreground text-sm">Nenhuma inspeção neste dia</div>}
          {dayEvents.map((e) => (
            <div key={e.id} className="border border-border rounded-md p-3">
              <div className="font-bold">{e.title}</div>
              <div className="text-xs text-muted-foreground mt-1 mono">{formatDateTime(e.scheduled_start)}</div>
              {e.address_city && <div className="text-xs text-muted-foreground mt-1">{e.address_city}/{e.address_state}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
